<template>
  <div class="modify-wrapper">

    <!-- ── Game selector ── -->
    <div v-if="selectorPhase === 'select'" class="selector-card">
      <h1 class="selector-title">Edit Game Config</h1>
      <p class="selector-subtitle">Select one of your games to edit its configuration.</p>

      <div v-if="loadingGames" class="status-msg">Loading your games…</div>

      <div v-else-if="loadError" class="message message-error">
        {{ loadError }}
        <button class="btn btn-secondary mt-sm" @click="fetchGames">Retry</button>
      </div>

      <div v-else-if="gamesList.length === 0" class="status-msg">
        You don't manage any games yet.
        <router-link to="/AddNewGame" class="link">Add a new game first.</router-link>
      </div>

      <div v-else class="selector-form">
        <div class="field-row">
          <label class="field-label">Game</label>
          <select v-model="selectedGameId" class="field-input">
            <option disabled value="">— Select a game —</option>
            <option v-for="g in gamesList" :key="g.IdGame" :value="g.IdGame">{{ g.GameName }}</option>
          </select>
        </div>

        <button
          class="btn btn-primary mt-sm"
          :disabled="!selectedGameId || fetchingConfig"
          @click="loadGame"
        >{{ fetchingConfig ? 'Loading config…' : 'Load & Edit' }}</button>

        <div v-if="fetchError" class="message message-error mt-sm">{{ fetchError }}</div>
      </div>
    </div>

    <!-- ── Wizard (once config is loaded) ── -->
    <GameConfigWizard
      v-else-if="selectorPhase === 'wizard'"
      mode="edit"
      :existingConfig="fetchedJson"
      :initialGameName="selectedGameName"
      :existingDataBasePath="existingDataBasePath"
      :existingGameId="selectedGameId"
      @reset="resetToSelector"
    />

  </div>
</template>

<script>
import GameConfigWizard from '@/components/GameConfigWizard.vue';

export default {
  name: 'ModifyGame',

  components: { GameConfigWizard },

  data() {
    return {
      selectorPhase: 'select', // 'select' | 'wizard'

      // Game list
      loadingGames: false,
      loadError: '',
      gamesList: [],

      // Selection
      selectedGameId: '',
      selectedGameName: '',

      // Config fetch
      fetchingConfig: false,
      fetchError: '',
      fetchedJson: null,
      existingDataBasePath: '',
    };
  },

  mounted() {
    this.fetchGames();
  },

  methods: {
    async fetchGames() {
      this.loadingGames = true;
      this.loadError = '';
      this.gamesList = [];
      try {
        const res = await fetch('/GetMyAdminGames/', {
          method: 'GET',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        const data = await res.json();
        if (data.success) {
          this.gamesList = data.games;
        } else {
          this.loadError = data.error || 'Failed to load your games.';
        }
      } catch (err) {
        this.loadError = 'Could not reach the server. Please check your connection.';
      } finally {
        this.loadingGames = false;
      }
    },

    async loadGame() {
      const game = this.gamesList.find(g => g.IdGame === this.selectedGameId);
      if (!game) return;

      this.fetchingConfig = true;
      this.fetchError = '';
      this.fetchedJson = null;

      const configUrl = game.GameVersionDataPath.replace('manifest.json', 'gameconfig.json');

      try {
        const res = await fetch(configUrl);
        if (res.status === 404) {
          this.fetchError = 'gameconfig.json not found in the repository. Make sure it has been uploaded.';
          return;
        }
        if (!res.ok) {
          this.fetchError = `Failed to fetch config (HTTP ${res.status}). Check that the repository is public.`;
          return;
        }
        const json = await res.json();
        this.fetchedJson = json;
        this.selectedGameName = game.GameName;
        this.existingDataBasePath = configUrl.replace('gameconfig.json', '');
        this.selectorPhase = 'wizard';
      } catch (err) {
        this.fetchError = 'Could not load the config file: ' + err.message;
      } finally {
        this.fetchingConfig = false;
      }
    },

    resetToSelector() {
      this.selectorPhase = 'select';
      this.fetchedJson = null;
      this.selectedGameId = '';
      this.selectedGameName = '';
      this.fetchError = '';
      this.existingDataBasePath = '';
    },
  },
};
</script>

<style scoped>
.modify-wrapper {
  min-height: 100vh;
  background: #0d1117;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
}

.selector-card {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 12px;
  padding: 2rem;
  width: 100%;
  max-width: 520px;
  color: #e0e0e0;
  margin-top: 2rem;
}

.selector-title {
  font-size: 1.6rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #58a6ff;
}

.selector-subtitle {
  color: #9ab;
  margin-bottom: 1.6rem;
  font-size: 0.95rem;
}

.selector-form {
  display: flex;
  flex-direction: column;
}

.status-msg {
  color: #8b949e;
  font-size: 0.9rem;
  padding: 0.5rem 0;
}

.link {
  color: #58a6ff;
  text-decoration: none;
  margin-left: 0.3rem;
}
.link:hover { text-decoration: underline; }

.field-row { display: flex; flex-direction: column; gap: 0.3rem; }
.field-label { font-weight: 600; font-size: 0.9rem; color: #c0c0d0; }

.field-input {
  background: #21262d;
  border: 1px solid #30363d;
  color: #e0e0e0;
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;
}
.field-input:focus { border-color: #58a6ff; }

.mt-sm { margin-top: 0.75rem; }

.btn { padding: 0.5rem 1.2rem; border-radius: 6px; border: none; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: opacity 0.2s; display: inline-block; }
.btn:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-primary { background: #2563eb; color: #fff; }
.btn-primary:hover:not(:disabled) { opacity: 0.85; }
.btn-secondary { background: #21262d; color: #cdd9e5; border: 1px solid #30363d; }
.btn-secondary:hover { opacity: 0.8; }

.message { padding: 0.65rem 1rem; border-radius: 6px; font-size: 0.88rem; }
.message-error { background: #2a1a1a; border: 1px solid #f85149; color: #ffb3ae; }
</style>
